import { format, addDays, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toZonedTime } from "date-fns-tz";

// Timezone constant for Peru (Lima)
const LIMA_TIMEZONE = "America/Lima";

/**
 * Centralized date utility service to ensure consistent date handling
 * with Lima, Peru timezone (GMT-5) throughout the application
 */
export const DateService = {
  /**
   * Converts any date to Lima timezone
   */
  toLimaTime: (date: Date | string): Date => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return toZonedTime(dateObj, LIMA_TIMEZONE);
  },

  /**
   * Gets the current date and time in Lima timezone
   */
  getCurrentLimaTime: (): Date => {
    return toZonedTime(new Date(), LIMA_TIMEZONE);
  },

  /**
   * Creates a date with only year, month, and day components in Lima timezone
   * This avoids timezone shifts when converting between date formats
   */
  createLimaDate: (year: number, month: number, day: number): Date => {
    // Use noon (12:00) to prevent date shifts due to timezone conversions
    return new Date(year, month, day, 12, 0, 0, 0);
  },

  /**
   * The most reliable way to parse a YYYY-MM-DD date string to a Date object
   * that won't shift days during timezone conversions
   */
  createDateFromApiFormat: (dateString: string): Date => {
    try {
      // First split the date string to get components
      const [year, month, day] = dateString.split("-").map(Number);

      // Validate the components
      if (
        isNaN(year) ||
        isNaN(month) ||
        isNaN(day) ||
        year < 1900 ||
        year > 2100 ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31
      ) {
        throw new Error(`Invalid date components: ${dateString}`);
      }

      // Create the date with noon as the time to avoid timezone issues
      // month-1 because JS months are zero-indexed
      const result = DateService.createLimaDate(year, month - 1, day);

      return result;
    } catch (error) {
      console.error("Error parsing API date format:", error, dateString);
      throw error;
    }
  },

  /**
   * Converts a UI date format (dd / MM / yyyy) to API format (yyyy-MM-dd)
   */
  convertToApiFormat: (uiDate: string): string | null => {
    try {
      if (uiDate.includes("/")) {
        // Format: dd/MM/yyyy or d/M/yyyy
        const parts = uiDate.split("/").map((part) => part.trim());
        if (parts.length !== 3) return null;

        const [day, month, year] = parts.map(Number);

        // Validate components
        if (
          isNaN(day) ||
          isNaN(month) ||
          isNaN(year) ||
          day < 1 ||
          day > 31 ||
          month < 1 ||
          month > 12 ||
          year < 1900
        ) {
          return null;
        }

        // Format to API standard YYYY-MM-DD with padding
        return `${year}-${month.toString().padStart(2, "0")}-${day
          .toString()
          .padStart(2, "0")}`;
      } else if (uiDate.includes("-")) {
        // Format: yyyy-MM-dd (already API format)
        // Validate it's properly formatted
        const [year, month, day] = uiDate.split("-").map(Number);
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
        return uiDate;
      }

      console.error("Unknown date format:", uiDate);
      return null;
    } catch (error) {
      console.error("Error converting date format:", error);
      return null;
    }
  },

  /**
   * Format a date string or Date object for display, with better error handling
   * @param date - Date string or object to format
   * @returns Formatted date string in format "mie. 24 de Abr 2025"
   */
  formatDeliveryDateForDisplay: (date: string | Date): string => {
    if (!date) return "Cargando fecha...";

    try {
      let dateObj: Date;

      if (typeof date === "string") {
        // Check if it's already in the display format
        if (date.includes(" de ")) return date;

        // Try to create a proper date object
        if (date.includes("T") && date.includes("Z")) {
          dateObj = parseISO(date);
        } else if (date.includes("-")) {
          const [year, month, day] = date.split("-").map(Number);
          dateObj = DateService.createLimaDate(year, month - 1, day);
        } else {
          dateObj = new Date(date);
        }
      } else {
        dateObj = date;
      }

      // Validate date object
      if (isNaN(dateObj.getTime())) {
        console.warn(
          "Invalid date passed to formatDeliveryDateForDisplay:",
          date
        );
        return "Cargando fecha..."; // Friendlier message during loading state
      }

      // Format as "mie. 24 de Abr 2025" (day abbr. day# de month abbr year)
      // Use date-fns with Spanish locale
      return format(dateObj, "EEE. d 'de' MMM yyyy", {
        locale: es,
      }).toLowerCase();
    } catch (error) {
      console.error("Error formatting date:", error, date);
      return "Cargando fecha..."; // Friendlier message during error state
    }
  },

  /**
   * Adds days to a date while preserving the time component
   */
  addDaysLima: (date: Date, days: number): Date => {
    return addDays(date, days);
  },

  /**
   * Converts a date to API format string (YYYY-MM-DD)
   */
  toLimaISOString: (date: Date): string => {
    // Extract date components and format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  },
};
